<template>
  <div class="w-full px-4">
    <!-- Desktop: Horizontal layout -->
    <div class="hidden lg:grid grid-cols-3 gap-6 max-w-5xl mx-auto">
      <div 
        v-for="(benefit, index) in benefits" 
        :key="index"
        class="relative bg-white dark:bg-gray-700 rounded-2xl shadow-lg overflow-hidden transition-transform hover:scale-105 hover:shadow-xl"
      >
        <!-- Header with gradient -->
        <div class="h-16 flex items-center justify-center" :class="benefit.headerBg">
          <div class="w-14 h-14 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center shadow-lg -mb-7 relative z-10">
            <font-awesome-icon :icon="benefit.icon" class="text-2xl" :class="benefit.iconColor" />
          </div>
        </div>
        
        <!-- Content -->
        <div class="pt-10 pb-6 px-6 text-center">
          <h4 class="text-lg font-bold text-gray-800 dark:text-white mb-4">
            {{ benefit.title }}
          </h4>
          
          <!-- Big number -->
          <div class="text-5xl font-bold mb-2" :class="benefit.valueColor">
            {{ benefit.value }}{{ benefit.suffix }}
          </div>
          
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {{ benefit.subtitle }}
          </p>
          
          <!-- Progress bar -->
          <div class="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
            <div 
              class="h-full rounded-full transition-all duration-1000"
              :class="benefit.progressColor"
              :style="{ width: benefit.value + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Mobile/Tablet: Stacked cards -->
    <div class="lg:hidden space-y-4 max-w-md mx-auto">
      <div 
        v-for="(benefit, index) in benefits" 
        :key="'mobile-' + index"
        class="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden"
      >
        <!-- Header -->
        <div class="h-12 flex items-center px-4 gap-3" :class="benefit.headerBg">
          <font-awesome-icon :icon="benefit.icon" class="text-xl text-white" />
          <h4 class="text-white font-bold">{{ benefit.title }}</h4>
        </div>
        
        <!-- Content -->
        <div class="p-4">
          <div class="flex items-center justify-between mb-3">
            <span class="text-3xl font-bold" :class="benefit.valueColor">
              {{ benefit.value }}{{ benefit.suffix }}
            </span>
            <span class="text-sm text-gray-500 dark:text-gray-400">
              {{ benefit.subtitle }}
            </span>
          </div>
          
          <!-- Progress bar -->
          <div class="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
            <div 
              class="h-full rounded-full"
              :class="benefit.progressColor"
              :style="{ width: benefit.value + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const benefits = computed(() => [
  {
    icon: 'fa-solid fa-clock',
    iconColor: 'text-green-500',
    title: t('charts.benefits.time_title'),
    value: 70,
    suffix: '%',
    subtitle: t('charts.benefits.time_subtitle'),
    headerBg: 'bg-gradient-to-r from-green-500 to-green-600',
    valueColor: 'text-green-500',
    progressColor: 'bg-gradient-to-r from-green-400 to-green-600'
  },
  {
    icon: 'fa-solid fa-trophy',
    iconColor: 'text-blue-500',
    title: t('charts.benefits.quality_title'),
    value: 100,
    suffix: '%',
    subtitle: t('charts.benefits.quality_subtitle'),
    headerBg: 'bg-gradient-to-r from-blue-500 to-blue-600',
    valueColor: 'text-blue-500',
    progressColor: 'bg-gradient-to-r from-blue-400 to-blue-600'
  },
  {
    icon: 'fa-solid fa-sack-dollar',
    iconColor: 'text-orange-500',
    title: t('charts.benefits.cost_title'),
    value: 50,
    suffix: '%',
    subtitle: t('charts.benefits.cost_subtitle'),
    headerBg: 'bg-gradient-to-r from-orange-500 to-orange-600',
    valueColor: 'text-orange-500',
    progressColor: 'bg-gradient-to-r from-orange-400 to-orange-600'
  }
]);
</script>

<style scoped>
</style>
